import React, { useEffect, useState } from "react";
import {
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useAuthUser } from "react-auth-kit";

const SERVICE_CUSTOM = "custom";

const SERVICE_OPTIONS = [
  { value: SERVICE_CUSTOM, label: "Custom host" },
  { value: "gmail", label: "Gmail" },
];

const defaultSmtpValues = {
  enabled: false,
  fromName: "MBID",
  fromEmail: "",
  credentialsConfigured: false,
  transport: {
    service: "gmail",
    host: "",
    port: 587,
    secure: false,
    auth: { user: "" },
  },
};

const EmailSmtpSetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const authUser = useAuthUser();
  const [smtp, setSmtp] = useState(defaultSmtpValues);
  const [authPass, setAuthPass] = useState("");
  const [testTo, setTestTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const setField = (field, value) => {
    setSmtp((prev) => ({ ...prev, [field]: value }));
  };

  const setTransportField = (field, value) => {
    setSmtp((prev) => ({
      ...prev,
      transport: { ...prev.transport, [field]: value },
    }));
  };

  const setAuthUserName = (value) => {
    setSmtp((prev) => ({
      ...prev,
      transport: {
        ...prev.transport,
        auth: { ...(prev.transport?.auth || {}), user: value },
      },
    }));
  };

  const applySmtpPayload = (data) => {
    if (!data) return;
    setSmtp({
      ...defaultSmtpValues,
      ...data,
      transport: {
        ...defaultSmtpValues.transport,
        ...(data.transport || {}),
        service: data.transport?.service || SERVICE_CUSTOM,
        auth: {
          ...defaultSmtpValues.transport.auth,
          ...(data.transport?.auth || {}),
        },
      },
    });
  };

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const res = await api.emailSettings.getSmtp();
      if (res?.success && res.data) applySmtpPayload(res.data);
      const email = authUser()?.email;
      if (email) setTestTo((current) => current || email);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const service =
        smtp.transport?.service === SERVICE_CUSTOM
          ? ""
          : smtp.transport?.service || "";
      const payload = {
        enabled: smtp.enabled === true,
        fromName: smtp.fromName,
        fromEmail: smtp.fromEmail,
        transport: {
          ...smtp.transport,
          service,
        },
      };
      if (authPass.trim()) payload.authPass = authPass.trim();
      const res = await api.emailSettings.saveSmtp(payload);
      if (res?.success) {
        setAuthPass("");
        applySmtpPayload(res.data);
        showNotification({
          title: t("Saved"),
          message: t("Email SMTP settings saved."),
          color: "green",
          icon: <IconCircleCheck />,
        });
        return;
      }
      showNotification({
        title: t("Error"),
        message:
          res?.error?.msg ||
          res?.message ||
          t("Could not save SMTP settings."),
        color: "red",
        icon: <IconExclamationMark />,
      });
    } catch (error) {
      showNotification({
        title: t("Error"),
        message: error.message,
        color: "red",
        icon: <IconExclamationMark />,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await api.emailSettings.testConnection({ to: testTo });
      if (res?.success) {
        showNotification({
          title: t("Test sent"),
          message: t("Check the inbox for {{email}}.", {
            email: res.data?.sentTo || testTo,
          }),
          color: "green",
          icon: <IconCircleCheck />,
        });
        return;
      }
      showNotification({
        title: t("Test failed"),
        message:
          res?.error?.msg ||
          res?.msg ||
          res?.message ||
          t("Could not send test email."),
        color: "red",
        icon: <IconExclamationMark />,
      });
    } catch (error) {
      showNotification({
        title: t("Test failed"),
        message: error.message,
        color: "red",
        icon: <IconExclamationMark />,
      });
    } finally {
      setTesting(false);
    }
  };

  const useCustomHost =
    !smtp.transport?.service || smtp.transport.service === SERVICE_CUSTOM;

  return (
    <Page>
      <PageListHeader
        title={t("Email SMTP Settings")}
        actionButtons={[
          {
            label: t("Send test email"),
            onClick: handleTest,
            loading: testing,
            disabled: loading,
            variant: "default",
          },
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("Sender")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {smtp.enabled
                ? t("Outbound mail uses the sender account saved on this page.")
                : t(
                    "When override is off, MBID uses SMTP credentials from the server config file."
                  )}
            </Text>
            <Switch
              label={t("Use database SMTP settings (override config file)")}
              checked={smtp.enabled === true}
              onChange={(event) =>
                setField("enabled", event.currentTarget.checked)
              }
              mb="md"
            />
            <TextInput
              label={t("From name")}
              value={smtp.fromName || ""}
              onChange={(e) => setField("fromName", e.currentTarget.value)}
              mb="md"
            />
            <TextInput
              label={t("From email")}
              value={smtp.fromEmail || ""}
              onChange={(e) => setField("fromEmail", e.currentTarget.value)}
            />
          </FormSectionCard>

          <FormSectionCard>
            <Title order={5}>{t("SMTP server")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t(
                "This global sender is used for AR form, MCA, notification, and user emails."
              )}
            </Text>
            <Select
              label={t("Provider")}
              data={SERVICE_OPTIONS}
              value={smtp.transport?.service || SERVICE_CUSTOM}
              onChange={(value) =>
                setTransportField("service", value || SERVICE_CUSTOM)
              }
              mb="md"
            />
            {useCustomHost ? (
              <>
                <TextInput
                  label={t("Host")}
                  value={smtp.transport?.host || ""}
                  onChange={(e) =>
                    setTransportField("host", e.currentTarget.value)
                  }
                  mb="md"
                />
                <NumberInput
                  label={t("Port")}
                  value={smtp.transport?.port ?? 587}
                  onChange={(value) => setTransportField("port", value)}
                  mb="md"
                />
                <Switch
                  label={t("Use TLS (secure)")}
                  checked={smtp.transport?.secure === true}
                  onChange={(event) =>
                    setTransportField("secure", event.currentTarget.checked)
                  }
                  mb="md"
                />
              </>
            ) : null}
            <TextInput
              label={t("SMTP username")}
              value={smtp.transport?.auth?.user || ""}
              onChange={(e) => setAuthUserName(e.currentTarget.value)}
              mb="md"
            />
            <TextInput
              label={t("SMTP password")}
              type="password"
              placeholder={
                smtp.credentialsConfigured
                  ? t("Leave blank to keep current password")
                  : t("Enter SMTP password")
              }
              value={authPass}
              onChange={(e) => setAuthPass(e.currentTarget.value)}
            />
            {smtp.credentialsConfigured ? (
              <Text size="xs" color="dimmed" mt="xs">
                {t("A password is already stored.")}
              </Text>
            ) : null}
          </FormSectionCard>

          <FormSectionCard>
            <Title order={5}>{t("Test connection")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t("Send a test message with the current SMTP settings.")}
            </Text>
            <TextInput
              label={t("Send test email to")}
              value={testTo}
              onChange={(e) => setTestTo(e.currentTarget.value)}
            />
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default EmailSmtpSetting;
